import React, { useState, useEffect, useMemo } from 'react';
import { Table, Loader, Message, Header, Icon, Button, Pagination, Form, Dropdown, Confirm, Popup, Menu } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import { formatTimestamp } from '../utils/timezone.utils';
import { highlightSearchTerms } from '../utils/requestHelpers';
import { useTimezone } from '../hooks/useTimezone';

const AllEventsView = () => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [allEventsLoaded, setAllEventsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(100);
  const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
  const [showFilters, setShowFilters] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [form3Filter, setForm3Filter] = useState('all'); // all | form3 | nonForm3
  const [providerFilter, setProviderFilter] = useState('all'); // all | AZURE | LINODE
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLast100ConfirmOpen, setDeleteLast100ConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  useEffect(() => {
    if (!globalSearch.trim()) {
      fetchEvents();
    }
  }, [currentPage, globalSearch]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * limit;
      const response = await api.admin.getEvents(limit, offset);
      
      if (response.data.success) {
        setEvents(response.data.events);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.data.message || 'Failed to fetch events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.message || 'Failed to fetch events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEventsForSearch = async () => {
    try {
      setLoading(true);
      setError(null);

      const batchSize = 1000;
      let offset = 0;
      let expectedTotal = null;
      const aggregatedEvents = [];

      while (true) {
        const response = await api.admin.getEvents(batchSize, offset);
        if (!response.data.success) {
          setError(response.data.message || 'Failed to fetch events');
          break;
        }

        const batch = response.data.events || [];
        aggregatedEvents.push(...batch);
        expectedTotal = response.data.totalCount;

        if (batch.length < batchSize) break;
        if (expectedTotal != null && aggregatedEvents.length >= expectedTotal) break;

        offset += batchSize;
      }

      setAllEvents(aggregatedEvents);
      setAllEventsLoaded(true);
    } catch (err) {
      console.error('Error fetching all events for global search:', err);
      setError(err.response?.data?.message || 'Failed to fetch events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (globalSearch.trim() && !allEventsLoaded) {
      fetchAllEventsForSearch();
    }
  }, [globalSearch, allEventsLoaded]);

  const eventTypeOptions = useMemo(
    () =>
      Array.from(new Set(events.map((e) => e.eventType)))
        .filter(Boolean)
        .sort()
        .map((type) => ({ key: type, value: type, text: type })),
    [events]
  );

  const filteredEvents = useMemo(() => {
    const normalizedSearch = globalSearch.trim().toLowerCase();

    const sourceEvents = normalizedSearch ? allEvents : events;

    return sourceEvents.filter((event) => {
      if (normalizedSearch) {
        const searchableText = [
          event.id,
          event.timestamp,
          formatTimestamp(event.timestamp),
          event.userEmail,
          event.eventType,
          event.requestId,
          event.eventDescription,
          event.metadata ? JSON.stringify(event.metadata) : ''
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchableText.includes(normalizedSearch)) {
          return false;
        }

        // Global search works independently across all events.
        // When search text is present, do not additionally restrict by panel filters.
        return true;
      }

      if (eventTypeFilter && event.eventType !== eventTypeFilter) {
        return false;
      }

      if (emailFilter && !event.userEmail?.toLowerCase().includes(emailFilter.toLowerCase())) {
        return false;
      }

      if (requestIdFilter && !event.requestId?.toLowerCase().includes(requestIdFilter.toLowerCase())) {
        return false;
      }

      const isForm3 = !!event.metadata?.isForm3;
      if (form3Filter === 'form3' && !isForm3) return false;
      if (form3Filter === 'nonForm3' && isForm3) return false;

      const provider = event.metadata?.provider;
      if (providerFilter !== 'all') {
        if (!provider || provider.toUpperCase() !== providerFilter) return false;
      }

      return true;
    });
  }, [events, allEvents, globalSearch, eventTypeFilter, emailFilter, requestIdFilter, form3Filter, providerFilter]);

  // Sorting state for events table
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('descending'); // 'ascending' | 'descending'

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
    } else {
      setSortColumn(column);
      // Default to descending for timestamp, ascending otherwise
      setSortDirection(column === 'timestamp' ? 'descending' : 'ascending');
    }
  };

  const sortedEvents = useMemo(() => {
    const items = filteredEvents.slice();
    const directionFactor = sortDirection === 'descending' ? -1 : 1;

    const getFieldValue = (event, column) => {
      switch (column) {
        case 'timestamp':
          return new Date(event.timestamp).getTime() || 0;
        case 'userEmail':
          return String(event.userEmail || '').toLowerCase();
        case 'eventType':
          return String(event.eventType || '').toLowerCase();
        case 'requestId':
          return String(event.requestId || '').toLowerCase();
        case 'description':
          return String(event.eventDescription || '').toLowerCase();
        default:
          return '';
      }
    };

    items.sort((a, b) => {
      const aVal = getFieldValue(a, sortColumn);
      const bVal = getFieldValue(b, sortColumn);

      if (aVal < bVal) return -1 * directionFactor;
      if (aVal > bVal) return 1 * directionFactor;
      return 0;
    });

    return items;
  }, [filteredEvents, sortColumn, sortDirection]);

  const isGlobalSearchActive = !!globalSearch.trim();
  const totalPages = Math.max(
    1,
    Math.ceil((isGlobalSearchActive ? filteredEvents.length : totalCount) / limit)
  );

  const hasActiveFilters = !!(
    eventTypeFilter ||
    emailFilter ||
    requestIdFilter ||
    form3Filter !== 'all' ||
    providerFilter !== 'all'
  );

  const activeFilterCount = [
    eventTypeFilter,
    emailFilter,
    requestIdFilter,
    form3Filter !== 'all' ? form3Filter : null,
    providerFilter !== 'all' ? providerFilter : null
  ].filter(Boolean).length;

  const activeSearchTerms = useMemo(
    () =>
      [
        globalSearch,
        eventTypeFilter,
        emailFilter,
        requestIdFilter,
        providerFilter !== 'all' ? providerFilter : null,
        form3Filter === 'form3' ? 'multi tenancy form' : null,
        form3Filter === 'nonForm3' ? 'other forms' : null
      ].filter((term) => term && String(term).trim()),
    [globalSearch, eventTypeFilter, emailFilter, requestIdFilter, providerFilter, form3Filter]
  );

  const handleClearFilters = () => {
    setGlobalSearch('');
    setEventTypeFilter(null);
    setEmailFilter('');
    setRequestIdFilter('');
    setForm3Filter('all');
    setProviderFilter('all');
  };

  const handleDeleteAll = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await api.admin.deleteAllEvents();
      setEvents([]);
      setAllEvents([]);
      setAllEventsLoaded(true);
      setTotalCount(0);
      setCurrentPage(1);
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting all events:', err);
      setError(err.response?.data?.message || 'Failed to delete all events. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteLast100 = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await api.admin.deleteLast100Events();
      // Refresh events after deletion
      setAllEventsLoaded(false);
      await fetchEvents();
      setDeleteLast100ConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting last 100 events:', err);
      setError(err.response?.data?.message || 'Failed to delete last 100 events. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  const getEventTypeColor = (eventType) => {
    const colors = {
      'REQUEST_CREATED': '#35B7F4',
      'SUB_APPROVER_APPROVED': '#6F9911',
      'MAIN_APPROVER_APPROVED': '#6F9911',
      'REQUEST_REJECTED': '#CE1B4E',
      'REQUEST_WITHDRAWN': '#F59E0B',
      'REQUEST_DELETED': '#CE1B4E',
      'REQUEST_UPDATED': '#6366F1',
      'USER_ADDED': '#6F9911',
      'USER_UPDATED': '#6366F1',
      'USER_DELETED': '#CE1B4E',
      'EXPIRING_LICENSES_VIEWED': '#0EA5E9',
      'EXPIRING_LICENSES_REPORT_DOWNLOADED': '#0284C7',
      'EXPIRING_LICENSES_IMPORTED': '#0284C7',
      'EXPIRING_LICENSES_DELETED': '#CE1B4E',
      'CLOUD_CAPACITY_VIEWED': '#0EA5E9',
      'CLOUD_CAPACITY_REPORT_DOWNLOADED': '#0284C7'
    };
    return colors[eventType] || '#6B7280';
  };

  const getEventTypeIcon = (eventType) => {
    const icons = {
      'REQUEST_CREATED': 'plus circle',
      'MAIN_APPROVER_APPROVED': 'check circle',
      'REQUEST_REJECTED': 'times circle',
      'REQUEST_WITHDRAWN': 'undo',
      'REQUEST_DELETED': 'trash',
      'REQUEST_UPDATED': 'edit',
      'USER_ADDED': 'user plus',
      'USER_UPDATED': 'user edit',
      'USER_DELETED': 'user times',
      'EXPIRING_LICENSES_VIEWED': 'eye',
      'EXPIRING_LICENSES_REPORT_DOWNLOADED': 'download',
      'EXPIRING_LICENSES_IMPORTED': 'upload',
      'EXPIRING_LICENSES_DELETED': 'trash',
      'CLOUD_CAPACITY_VIEWED': 'eye',
      'CLOUD_CAPACITY_REPORT_DOWNLOADED': 'download'
    };
    return icons[eventType] || 'circle';
  };

  if (loading && events.length === 0) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
        <Loader active inline='centered' size='large'>
          Loading events...
        </Loader>
      </div>
    );
  }

  return (
    <div className="page-fade-in" style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 200px)',
      minHeight: '600px',
      position: 'relative',
      padding: '0 2rem'
    }}>
      {/* Fixed Header */}
      <div style={{
        flexShrink: 0,
        backgroundColor: 'white',
        zIndex: 100,
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1.35rem',
              fontWeight: 700,
              color: '#062D4D'
            }}
          >
            <Icon name='history' color='blue' />
            <span>All Application Events</span>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Popup
              on='click'
              position='bottom left'
              pinned
              mouseLeaveDelay={0}
              offset={[0, 8]}
              style={{ zIndex: 2000 }}
              open={actionsMenuOpen}
              onOpen={() => setActionsMenuOpen(true)}
              onClose={() => setActionsMenuOpen(false)}
              trigger={
                <Button
                  icon
                  labelPosition='left'
                  disabled={isDeleting || totalCount === 0}
                  loading={isDeleting}
                >
                  <Icon name='cog' />
                  Actions
                </Button>
              }
              content={
                <Menu vertical secondary size='small' style={{ minWidth: 180, fontSize: '0.9em' }}>
                  <Menu.Item 
                    style={{ padding: '6px 10px', color: '#CE1B4E' }}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setDeleteConfirmOpen(true);
                    }}
                    disabled={isDeleting || totalCount === 0}
                  >
                    <Icon name='trash' size='small' /> Delete All
                  </Menu.Item>
                  <Menu.Item 
                    style={{ padding: '6px 10px', color: '#CE1B4E' }}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setDeleteLast100ConfirmOpen(true);
                    }}
                    disabled={isDeleting || totalCount === 0}
                  >
                    <Icon name='trash' size='small' /> Delete Last 100
                  </Menu.Item>
                </Menu>
              }
            />
            <Button
              icon
              labelPosition='left'
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? 'blue' : undefined}
              primary={hasActiveFilters && !showFilters}
            >
              <Icon name='filter' />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {hasActiveFilters && !showFilters && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '8px',
                  backgroundColor: '#fff',
                  color: '#2185d0',
                  fontSize: '11px',
                  fontWeight: 700,
                  minWidth: '22px',
                  height: '22px',
                  borderRadius: '12px',
                  padding: '0 7px',
                  lineHeight: 1,
                  letterSpacing: '0.3px'
                }}>
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        <Form style={{ marginBottom: '0.75rem' }}>
          <Form.Input
            label='Global Search'
            placeholder='Search across all event fields...'
            value={globalSearch}
            onChange={(e, { value }) => {
              setGlobalSearch(value);
              setCurrentPage(1);
            }}
          />
        </Form>

        {/* Filters (collapsible) */}
        {showFilters && (
          <Form
            style={{
              marginBottom: '16px',
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '4px',
              border: '1px solid #e1e8ed',
              zIndex: 99,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <Form.Group widths='equal'>
              <Form.Input
                label='User Email'
                placeholder='Filter by email...'
                value={emailFilter}
                onChange={(e, { value }) => {
                  setEmailFilter(value);
                  setCurrentPage(1);
                }}
              />
              <Form.Input
                label='Request ID'
                placeholder='Filter by request id...'
                value={requestIdFilter}
                onChange={(e, { value }) => {
                  setRequestIdFilter(value);
                  setCurrentPage(1);
                }}
              />
              <Form.Field>
                <label>Event Type</label>
                <Dropdown
                  placeholder='All types'
                  fluid
                  selection
                  clearable
                  options={eventTypeOptions}
                  value={eventTypeFilter}
                  onChange={(e, { value }) => {
                    setEventTypeFilter(value || null);
                    setCurrentPage(1);
                  }}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group widths='equal'>
              <Form.Field>
                <label>Form Type</label>
                <Button
                  size='small'
                  basic={form3Filter !== 'all'}
                  primary={form3Filter === 'all'}
                  onClick={() => {
                    setForm3Filter('all');
                    setCurrentPage(1);
                  }}
                >
                  All
                </Button>
                <Button
                  size='small'
                  basic={form3Filter !== 'form3'}
                  primary={form3Filter === 'form3'}
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    setForm3Filter('form3');
                    setCurrentPage(1);
                  }}
                >
                  Multi Tenancy Form
                </Button>
                <Button
                  size='small'
                  basic={form3Filter !== 'nonForm3'}
                  primary={form3Filter === 'nonForm3'}
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    setForm3Filter('nonForm3');
                    setCurrentPage(1);
                  }}
                >
                  Other Forms
                </Button>
              </Form.Field>

              <Form.Field>
                <label>Provider</label>
                <Button
                  size='small'
                  basic={providerFilter !== 'all'}
                  primary={providerFilter === 'all'}
                  onClick={() => {
                    setProviderFilter('all');
                    setCurrentPage(1);
                  }}
                >
                  All
                </Button>
                <Button
                  size='small'
                  basic={providerFilter !== 'AZURE'}
                  primary={providerFilter === 'AZURE'}
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    setProviderFilter('AZURE');
                    setCurrentPage(1);
                  }}
                >
                  Azure
                </Button>
                <Button
                  size='small'
                  basic={providerFilter !== 'LINODE'}
                  primary={providerFilter === 'LINODE'}
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    setProviderFilter('LINODE');
                    setCurrentPage(1);
                  }}
                >
                  Linode
                </Button>
              </Form.Field>

              <Form.Field>
                <label>&nbsp;</label>
                <Button
                  size='small'
                  basic
                  icon
                  labelPosition='left'
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <Icon name='erase' />
                  Clear Filters
                </Button>
              </Form.Field>
            </Form.Group>
          </Form>
        )}
      </div>

      {/* Scrollable Table Container */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'auto',
        minHeight: 0,
        position: 'relative'
      }}>
        {error && (
          <Message negative style={{ marginBottom: '1rem' }}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
            <Button size='small' onClick={fetchEvents} style={{ marginTop: '0.5rem' }}>
              Retry
            </Button>
          </Message>
        )}

        {filteredEvents.length === 0 && !loading ? (
          <Message info>
            <Message.Header>No Events Found</Message.Header>
            <p>
              {(globalSearch.trim() || hasActiveFilters)
                ? 'No records match the selected search/filter criteria.'
                : 'No activity events have been recorded yet.'}
            </p>
          </Message>
        ) : (
          <>
            <Table celled striped sortable style={{ marginBottom: '1.5rem' }}>
              <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <Table.Row>
                  <Table.HeaderCell
                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                    width={2}
                    sorted={sortColumn === 'timestamp' ? sortDirection : null}
                    onClick={() => handleSort('timestamp')}
                  >
                    Timestamp
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                    width={2}
                    sorted={sortColumn === 'userEmail' ? sortDirection : null}
                    onClick={() => handleSort('userEmail')}
                  >
                    User Email
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                    width={2}
                    sorted={sortColumn === 'eventType' ? sortDirection : null}
                    onClick={() => handleSort('eventType')}
                  >
                    Event Type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                    width={1}
                    sorted={sortColumn === 'requestId' ? sortDirection : null}
                    onClick={() => handleSort('requestId')}
                  >
                    Request ID
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                    sorted={sortColumn === 'description' ? sortDirection : null}
                    onClick={() => handleSort('description')}
                  >
                    Description
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(isGlobalSearchActive
                  ? sortedEvents.slice((currentPage - 1) * limit, currentPage * limit)
                  : sortedEvents
                ).map((event) => (
                  <Table.Row key={event.id}>
                    <Table.Cell>
                      <div style={{ fontSize: '0.9em' }}>
                        {highlightSearchTerms(formatTimestamp(event.timestamp), activeSearchTerms)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ fontWeight: 500 }}>
                        {highlightSearchTerms(event.userEmail || '', activeSearchTerms)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon
                          name={getEventTypeIcon(event.eventType)}
                          style={{ color: getEventTypeColor(event.eventType) }}
                        />
                        <span style={{ fontSize: '0.9em' }}>
                          {highlightSearchTerms(event.eventType || '', activeSearchTerms)}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {event.requestId ? (
                        <code style={{ fontSize: '0.85em', background: '#f3f4f6', padding: '2px 6px', borderRadius: '3px' }}>
                          {highlightSearchTerms(event.requestId, activeSearchTerms)}
                        </code>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>N/A</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div>{highlightSearchTerms(event.eventDescription || '', activeSearchTerms)}</div>
                      {event.metadata && (
                        <div style={{ fontSize: '0.85em', color: '#6B7280', marginTop: '4px' }}>
                          {event.metadata.category && (
                            <>{highlightSearchTerms(`Category: ${event.metadata.category}`, activeSearchTerms)}</>
                          )}
                          {event.metadata.cloudName && (
                            <>{highlightSearchTerms(` | Cloud: ${event.metadata.cloudName}`, activeSearchTerms)}</>
                          )}
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '1rem' }}>
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => setCurrentPage(activePage)}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                  nextItem={{ content: <Icon name='chevron right' />, icon: true }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Confirm
        open={deleteConfirmOpen}
        onConfirm={handleDeleteAll}
        onCancel={() => setDeleteConfirmOpen(false)}
        content={`Are you sure you want to delete all ${totalCount} event(s)? This action cannot be undone.`}
        confirmButton={{ content: 'Delete All', negative: true }}
        cancelButton="Cancel"
        size="small"
      />

      <Confirm
        open={deleteLast100ConfirmOpen}
        onConfirm={handleDeleteLast100}
        onCancel={() => setDeleteLast100ConfirmOpen(false)}
        content={`Are you sure you want to delete the oldest 100 event(s)? This action cannot be undone.`}
        confirmButton={{ content: 'Delete Last 100', negative: true }}
        cancelButton="Cancel"
        size="small"
      />
    </div>
  );
};

export default AllEventsView;

